import React from 'react'
import { Alert } from 'react-bootstrap'

import { Spinner } from '../../../../components/Spinner/Spinner'

import { type CrmEligibility } from './useCrmEligibility'

export const LARGE_BATCH_THRESHOLD = 50

interface CrmLoadingBodyProps {
  crmName: string
}

export const CrmLoadingBody: React.FC<CrmLoadingBodyProps> = ({ crmName }) => (
  <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5'>
    <Spinner />
    <div className='paragraph-medium medium'>Checking {crmName} eligibility…</div>
  </div>
)

interface CrmNoEligibleAlertProps {
  crmName: string
}

export const CrmNoEligibleAlert: React.FC<CrmNoEligibleAlertProps> = ({ crmName }) => (
  <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
    <i className='bi bi-info-circle' />
    <span>
      None of the selected clients are synced with {crmName}. Add their {crmName} ID in client profiles to proceed.
    </span>
  </Alert>
)

interface CrmEligibilityNoticeProps {
  eligibility: CrmEligibility
  verbPhrase: string
  crmName: string
}

export const CrmEligibilityNotice: React.FC<CrmEligibilityNoticeProps> = ({ eligibility, verbPhrase, crmName }) => {
  if (eligibility.status === 'error') {
    return (
      <Alert variant='danger' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
        <i className='bi bi-exclamation-triangle' />
        <span>We couldn't check {crmName} eligibility for the selected clients. Please close this dialog and try again.</span>
      </Alert>
    )
  }

  if (eligibility.status !== 'ready' || eligibility.eligibleCount === 0) {
    return null
  }

  const largeBatch = eligibility.totalCount >= LARGE_BATCH_THRESHOLD
  const hasSkipped = eligibility.skippedCount > 0
  if (!largeBatch && !hasSkipped) {
    return null
  }

  return (
    <Alert variant='yellow' className='mb-0 d-flex align-items-start gap-2 paragraph-small'>
      <i className='bi bi-info-circle' />
      <span>
        {hasSkipped ? (
          <>
            You'll {verbPhrase}{' '}
            <strong>
              {eligibility.eligibleCount} of {eligibility.totalCount}
            </strong>{' '}
            clients in {crmName} (<strong>{eligibility.skippedCount}</strong> not synced with Zocks).
          </>
        ) : (
          <>
            You are planning to {verbPhrase} <strong>{eligibility.totalCount}</strong> clients in {crmName}.
          </>
        )}
        {largeBatch ? " This action can't be undone or stopped once started." : null}
      </span>
    </Alert>
  )
}
